/**
 * Shared connection and credential verification utilities
 */

export async function verifyConnection({
  connectionId,
  verificationStep,
  formData,
  connectionType,
}) {
  const verifyFormData = new FormData();
  for (let [key, value] of formData.entries()) {
    if (
      key !== "connection_action" &&
      key !== "verification_step" &&
      key !== "_method"
    ) {
      verifyFormData.append(key, value);
    }
  }
  verifyFormData.set("connection[type]", connectionType);
  if (verificationStep) {
    verifyFormData.set("verification_step", verificationStep);
  }

  const verifyResponse = await fetch(
    `/admin/datacloud/connections/${connectionId}/verify_credentials`,
    {
      method: "POST",
      body: verifyFormData,
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!verifyResponse.ok) {
    const verifyData = await verifyResponse.json();
    return {
      success: false,
      error: {
        errorMessage:
          verifyData.error_message ||
          "There was an error verifying the connection",
        errorLink: verifyData.error_link || "",
        curlCommand: verifyData.curl_command || "",
        tokenForCurl: verifyData.token_for_curl || "",
        rawMessage: verifyData.raw_message || "",
      },
    };
  }
  return { success: true };
}

export async function verifyCredential({
  connectionId,
  credentialId = null,
  verificationStep,
  formData,
}) {
  const verifyFormData = new FormData();
  for (let [key, value] of formData.entries()) {
    if (
      key !== "connection_action" &&
      key !== "verification_step" &&
      key !== "_method"
    ) {
      verifyFormData.append(key, value);
    }
  }

  if (credentialId) {
    verifyFormData.set("id", credentialId);
  }

  if (verificationStep) {
    verifyFormData.set("verification_step", verificationStep);
  }

  const verifyResponse = await fetch(
    `/admin/datacloud/connections/${connectionId}/credentials/verify`,
    {
      method: "POST",
      body: verifyFormData,
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!verifyResponse.ok) {
    const verifyData = await verifyResponse.json();
    return {
      success: false,
      error: {
        errorMessage:
          verifyData.error_message ||
          "There was an error verifying the credential",
        errorLink: verifyData.error_link || "",
        curlCommand: verifyData.curl_command || "",
        tokenForCurl: verifyData.token_for_curl || "",
        rawMessage: verifyData.raw_message || "",
      },
    };
  }
  return { success: true };
}

export async function runStepByStepVerification({
  connectionId,
  verificationSteps,
  formData,
  connectionType,
  onStepChange,
}) {
  const totalSteps = verificationSteps.length;
  for (let i = 0; i < totalSteps; i++) {
    const step = i + 1;
    const stepLabel = verificationSteps[i].label;
    if (onStepChange) {
      onStepChange({ currentStep: step, totalSteps, stepLabel });
    }
    const result = await verifyConnection({
      connectionId,
      verificationStep: step,
      formData,
      connectionType,
    });
    if (!result.success) {
      return result;
    }
  }
  return { success: true };
}

/**
 * Credentials V2 flow for create page:
 * 1. Verify credentials via CredentialsController
 * 2. Create credential via CredentialsController
 *
 * Used for connection types that support multiple credentials (bitbucket, github, jira_cloud, etc.)
 */
export async function runCredentialsV2Flow({
  connectionId,
  verificationSteps,
  formData,
  onStepChange,
}) {
  const totalSteps = verificationSteps.length + 1; // +1 for the create step

  // Step 1: Run verification steps via credentials endpoint
  for (let i = 0; i < verificationSteps.length; i++) {
    const step = i + 1;
    const stepLabel = verificationSteps[i].label;
    if (onStepChange) {
      onStepChange({ currentStep: step, totalSteps, stepLabel });
    }

    const result = await verifyCredential({
      connectionId,
      credentialId: null, // New credential
      verificationStep: step,
      formData,
    });

    if (!result.success) {
      return result;
    }
  }

  // Step 2: Create the credential
  if (onStepChange) {
    onStepChange({
      currentStep: totalSteps,
      totalSteps,
      stepLabel: "Saving credential...",
    });
  }

  try {
    const createFormData = new FormData();
    for (let [key, value] of formData.entries()) {
      if (
        key !== "connection_action" &&
        key !== "verification_step" &&
        key !== "_method"
      ) {
        createFormData.append(key, value);
      }
    }

    const response = await fetch(
      `/admin/datacloud/connections/${connectionId}/credentials`,
      {
        method: "POST",
        body: createFormData,
        headers: {
          Accept: "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.ok === false) {
      return {
        success: false,
        error: {
          errorMessage:
            data.display_message ||
            data.error ||
            data.message ||
            "Failed to create credential",
        },
      };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: {
        errorMessage: "Failed to create credential",
      },
    };
  }
}
